import { lazy, Suspense } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { ErrorBoundary } from './components/common'
import { StarryBackground } from './components/layout/StarryBackground'
import { useDataSync } from './hooks/useDataSync'

// Dev tools (only active in dev mode)
import './utils/devTools'

// Eager load the welcome page for fast initial render
import Welcome from './pages/Welcome'

// Lazy load other pages for code splitting
const YearReviewPage = lazy(() => import('./pages/YearReviewPage'))
const ReviewPage = lazy(() => import('./pages/ReviewPage'))
const ExerciseHub = lazy(() => import('./pages/ExerciseHub'))
const HoroscopePage = lazy(() => import('./pages/HoroscopePage'))
const ThemeDiscoveryPage = lazy(() => import('./pages/ThemeDiscoveryPage'))
const VisionPage = lazy(() => import('./pages/VisionPage'))
const PhotoMemoriesPage = lazy(() => import('./pages/PhotoMemoriesPage'))
const IntentionsPage = lazy(() => import('./pages/IntentionsPage'))
const GenerateWrappedPage = lazy(() => import('./pages/GenerateWrappedPage'))
const SummaryPage = lazy(() => import('./pages/SummaryPage'))
const SharedSummaryPage = lazy(() => import('./pages/SharedSummaryPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const NotFound = lazy(() => import('./pages/NotFound'))
const AdminPage = lazy(() => import('./pages/AdminPage'))

// Kids mode pages
const KidsHub = lazy(() => import('./pages/kids/KidsHub'))
const KidsMagicStars = lazy(() => import('./pages/kids/KidsMagicStars'))
const KidsEmojiYear = lazy(() => import('./pages/kids/KidsEmojiYear'))
const KidsDreamPicker = lazy(() => import('./pages/kids/KidsDreamPicker'))
const KidsSuperPower = lazy(() => import('./pages/kids/KidsSuperPower'))
const KidsSummary = lazy(() => import('./pages/kids/KidsSummary'))

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <span className="text-4xl block animate-pulse">✨</span>
        <p className="text-cream/60 text-sm">Loading...</p>
      </div>
    </div>
  )
}

export default function App() {
  const location = useLocation()

  // Sync data with D1 database
  useDataSync()

  return (
    <ErrorBoundary>
      {/* Animated starry background */}
      <StarryBackground />

      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Welcome />} />
            <Route path="/year-review" element={<YearReviewPage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/hub" element={<ExerciseHub />} />
            <Route path="/horoscope" element={<HoroscopePage />} />
            <Route path="/theme" element={<ThemeDiscoveryPage />} />
            <Route path="/words" element={<ThemeDiscoveryPage />} /> {/* Legacy route */}
            <Route path="/vision" element={<VisionPage />} />
            <Route path="/photos" element={<PhotoMemoriesPage />} />
            <Route path="/intentions" element={<IntentionsPage />} />
            <Route path="/generate" element={<GenerateWrappedPage />} />
            <Route path="/summary" element={<SummaryPage />} />
            <Route path="/shared/:id" element={<SharedSummaryPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            {/* Kids mode routes */}
            <Route path="/kids" element={<KidsHub />} />
            <Route path="/kids/magic-stars" element={<KidsMagicStars />} />
            <Route path="/kids/emoji-year" element={<KidsEmojiYear />} />
            <Route path="/kids/dream-picker" element={<KidsDreamPicker />} />
            <Route path="/kids/super-power" element={<KidsSuperPower />} />
            <Route path="/kids/summary" element={<KidsSummary />} />
            {/* Admin */}
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </ErrorBoundary>
  )
}
